/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   Filters selectors
 */

import { DEFAULT_FILTERS } from '@zcode-ui/filters'
import { getFilterCount } from '@zcode/nr/es/shared/utils/filter'
import type { State } from '@zcode/state-type'
import deepequal from 'fast-deep-equal'
import { compose } from 'redux'
import {
  selectListablesCount,
  selectListablesCountData,
  selectListablesCountError,
  selectListablesCountFetchingKey,
  selectListablesCountIsFetching,
  selectListablesCountMeta,
} from './listablesCount'
import { selectDirty } from './meta'

export const selectRoot = (state: State) => state.filters
export const selectFilters = compose((state) => state?.filters || {}, selectRoot)

export const selectNeighborhoodIds = compose((filters) => filters.neighborhoodIds, selectFilters)

export const selectFilterCount = compose((filters) => getFilterCount(filters), selectFilters)

export const selectAnyFilterSet = (state: State) => !selectDirty(state)

export const selectNeighborhoodSelected = (state: State, neighborhoodId: number) => {
  const neighborhoodIds = selectNeighborhoodIds(state)
  return neighborhoodIds.includes(neighborhoodId)
}

export const selectFilterBedroom = compose((filters) => {
  return filters?.bedrooms ?? null
}, selectFilters)

export const selectFilterMinBathroom = compose((filters) => {
  return filters?.minBathrooms ?? null
}, selectFilters)

export const selectHasLowLevelFilter = compose((filtersProp) => {
  const filters = { ...filtersProp }
  filters.bedrooms = DEFAULT_FILTERS.bedrooms
  filters.minPrice = DEFAULT_FILTERS.minPrice
  filters.maxPrice = DEFAULT_FILTERS.maxPrice
  return !deepequal(filters, DEFAULT_FILTERS)
}, selectFilters)

export {
  selectListablesCount,
  selectListablesCountData,
  selectListablesCountError,
  selectListablesCountFetchingKey,
  selectListablesCountIsFetching,
  selectListablesCountMeta,
}

export { selectFilterModalType } from './filterModal'
export { selectIsFilterModalOpen } from './filterModal'

export const selectHasShortTermFilter = (state: State) => {
  const filters = selectFilters(state) || {}
  return !!filters?.shortTerm
}

export const selectHasLongTermFilter = (state: State) => {
  const filters = selectFilters(state) || {}
  return !!filters?.longTerm
}

export const selectFilterStartDate = (state: State) => {
  return selectFilters(state)?.startDate
}

export const selectFilterEndDate = (state: State) => {
  return selectFilters(state)?.endDate
}

export const selectFilterGuests = (state: State) => {
  return selectFilters(state)?.guests
}

export const selectIsSafeToUpdateFilters = (state: State) => {
  // longTerm will be true or false if filters are active; null if the page is SSR
  return !(selectFilters(state)?.longTerm === null)
}

export const selectPropertyTypes = compose((state) => state.propertyTypes, selectFilters)

export const selectListingAmenities = compose((state) => state.listingAmenities, selectFilters)

export const selectOrAmenities = compose((state) => state.orAmenities, selectFilters)
